import React from 'react';
import { Box, Card, Skeleton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { chartColors } from '../theme';

export const fmtNumber = (n) =>
  Number.isFinite(Number(n)) && n !== null ? new Intl.NumberFormat().format(Number(n)) : '—';

export const fmtPercent = (part, total) => {
  const p = Number(part);
  const t = Number(total);
  if (!t || !Number.isFinite(p)) return '—';
  const v = (p / t) * 100;
  return `${v >= 99.95 || v === 0 ? v.toFixed(0) : v.toFixed(1)}%`;
};

export const fmtMs = (ms) => {
  const n = Number(ms);
  if (ms == null || !Number.isFinite(n)) return '—';
  return n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)} s` : `${Math.round(n)} ms`;
};

export function useChartColors() {
  const theme = useTheme();
  return chartColors[theme.palette.mode];
}

/** Card with a quiet title row. */
export function Panel({ title, subtitle, action, children, sx, contentSx }) {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', ...sx }}>
      {title ? (
        <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ px: 2.5, pt: 2.25, pb: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{title}</Typography>
            {subtitle ? (
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>{subtitle}</Typography>
            ) : null}
          </Box>
          {action}
        </Stack>
      ) : null}
      <Box sx={{ px: 2.5, pb: 2.25, flex: 1, minWidth: 0, ...contentSx }}>{children}</Box>
    </Card>
  );
}

/** Single headline number. `tone` colors only the small status dot, never the number. */
export function StatTile({ label, value, hint, loading, tone, icon: Icon }) {
  const colors = useChartColors();
  const dot = tone ? colors[tone] : null;
  return (
    <Card sx={{ px: 2.25, py: 2 }}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        {dot ? <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dot, flexShrink: 0 }} /> : null}
        <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'text.secondary', fontWeight: 500 }}>
          {label}
        </Typography>
        {Icon ? (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.25,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              color: 'primary.main',
              bgcolor: 'action.selected',
            }}
          >
            <Icon sx={{ fontSize: 18 }} />
          </Box>
        ) : null}
      </Stack>
      {loading ? (
        <Skeleton width="55%" height={38} />
      ) : (
        <Typography sx={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.35, mt: 0.5 }}>
          {value}
        </Typography>
      )}
      <Typography noWrap sx={{ fontSize: 12, color: 'text.disabled', minHeight: 18 }}>
        {loading ? '' : hint || ''}
      </Typography>
    </Card>
  );
}

export function Legend({ series }) {
  return (
    <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
      {series.map((s) => (
        <Stack key={s.key} direction="row" alignItems="center" spacing={0.75}>
          <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: s.color }} />
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{s.label}</Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function useWidth() {
  const ref = React.useRef(null);
  const [width, setWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/** Smallest "nice" axis top (1, 1.5, 2, 2.5, 3, 4, 5, 6, 8 × 10^k) above the peak. */
const niceMax = (v) => {
  if (v <= 0) return 4;
  const target = v * 1.08;
  const pow = 10 ** Math.floor(Math.log10(target));
  const step = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find((s) => s * pow >= target);
  return step * pow;
};

/**
 * Stacked vertical bars with a hover tooltip per column.
 * data: [{ label, tooltipLabel?, [seriesKey]: number }]
 * series: [{ key, label, color }] - bottom to top.
 */
export function StackedBarChart({ data, series, height = 220, loading, emptyText, formatValue = fmtNumber }) {
  const theme = useTheme();
  const ink = theme.custom;
  const [ref, width] = useWidth();
  const [hover, setHover] = React.useState(null);

  const padL = 36;
  const padR = 4;
  const padT = 8;
  const padB = 22;
  const innerW = Math.max(0, width - padL - padR);
  const innerH = height - padT - padB;

  const totals = (data || []).map((d) => series.reduce((a, s) => a + (Number(d[s.key]) || 0), 0));
  const max = niceMax(Math.max(0, ...totals));
  const n = data?.length || 0;
  const slot = n ? innerW / n : 0;
  const barW = Math.max(2, Math.min(28, slot * 0.68));
  const y = (v) => padT + innerH - (v / max) * innerH;
  const ticks = [0, max / 2, max];
  const labelEvery = Math.max(1, Math.ceil(n / Math.max(1, Math.floor(innerW / 56))));
  const allZero = totals.every((v) => v === 0);

  return (
    <Box ref={ref} sx={{ position: 'relative', width: '100%' }}>
      {loading || !width ? (
        <Skeleton variant="rounded" height={height} />
      ) : (
        <>
          <svg width={width} height={height} role="img" style={{ display: 'block' }}>
            {ticks.map((tv) => (
              <g key={tv}>
                <line x1={padL} x2={width - padR} y1={y(tv)} y2={y(tv)} stroke={tv === 0 ? ink.inkMuted : ink.grid} strokeOpacity={tv === 0 ? 0.6 : 1} />
                <text x={padL - 8} y={y(tv) + 4} textAnchor="end" fontSize="11" fill={ink.inkMuted} style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatValue(tv)}
                </text>
              </g>
            ))}
            {(data || []).map((d, i) => {
              const cx = padL + slot * i + slot / 2;
              let acc = 0;
              const segs = series
                .map((s) => {
                  const v = Number(d[s.key]) || 0;
                  const top = acc + v;
                  const seg = { key: s.key, color: s.color, y0: y(acc), y1: y(top), v };
                  acc = top;
                  return seg;
                })
                .filter((s) => s.v > 0);
              const active = hover?.i === i;
              return (
                <g key={d.label + i}>
                  {active ? <rect x={cx - slot / 2} y={padT} width={slot} height={innerH} fill={ink.inkMuted} opacity={0.08} /> : null}
                  {segs.map((s, si) => {
                    const isTop = si === segs.length - 1;
                    const h = Math.max(1, s.y0 - s.y1 - (si > 0 ? 2 : 0));
                    const r = isTop ? Math.min(4, barW / 2, h) : 0;
                    const x0 = cx - barW / 2;
                    const yTop = s.y1;
                    const yBot = yTop + h;
                    const path = `M${x0},${yBot} L${x0},${yTop + r} Q${x0},${yTop} ${x0 + r},${yTop} L${x0 + barW - r},${yTop} Q${x0 + barW},${yTop} ${x0 + barW},${yTop + r} L${x0 + barW},${yBot} Z`;
                    return <path key={s.key} d={path} fill={s.color} />;
                  })}
                  {i % labelEvery === 0 ? (
                    <text x={cx} y={height - 6} textAnchor="middle" fontSize="11" fill={ink.inkMuted}>
                      {d.label}
                    </text>
                  ) : null}
                  <rect
                    x={cx - slot / 2}
                    y={padT}
                    width={slot}
                    height={innerH}
                    fill="transparent"
                    onMouseEnter={() => setHover({ i, x: cx })}
                    onMouseLeave={() => setHover(null)}
                  />
                </g>
              );
            })}
          </svg>
          {allZero && emptyText ? (
            <Typography
              sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', fontSize: 13, color: 'text.disabled', pointerEvents: 'none' }}
            >
              {emptyText}
            </Typography>
          ) : null}
          {hover && data?.[hover.i] ? (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: Math.min(Math.max(hover.x - 80, 0), Math.max(0, width - 170)),
                width: 170,
                pointerEvents: 'none',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
                px: 1.5,
                py: 1,
              }}
            >
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>
                {data[hover.i].tooltipLabel || data[hover.i].label}
              </Typography>
              {[...series].reverse().map((s) => (
                <Stack key={s.key} direction="row" alignItems="center" spacing={0.75}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: s.color }} />
                  <Typography sx={{ fontSize: 12.5, flex: 1 }}>{s.label}</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {formatValue(data[hover.i][s.key] || 0)}
                  </Typography>
                </Stack>
              ))}
            </Box>
          ) : null}
        </>
      )}
    </Box>
  );
}

/** Ranked horizontal bars: label, value, optional secondary text. */
export function BarList({ items, color, loading, emptyText, formatValue = fmtNumber }) {
  const max = Math.max(1, ...(items || []).map((i) => Number(i.value) || 0));
  if (loading) {
    return (
      <Stack spacing={1.25}>
        {[0, 1, 2, 3].map((k) => (
          <Skeleton key={k} variant="rounded" height={26} />
        ))}
      </Stack>
    );
  }
  if (!items?.length) {
    return <Typography sx={{ fontSize: 13, color: 'text.disabled', py: 3, textAlign: 'center' }}>{emptyText}</Typography>;
  }
  return (
    <Stack spacing={0.75}>
      {items.map((it) => (
        <Box key={it.key ?? it.label} title={it.title} sx={{ position: 'relative', height: 30, display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${Math.max(1, ((Number(it.value) || 0) / max) * 100)}%`,
              bgcolor: color,
              opacity: 0.16,
              borderRadius: 1,
            }}
          />
          <Typography noWrap sx={{ position: 'relative', fontSize: 13, px: 1.25, flex: 1, minWidth: 0 }}>
            {it.label}
          </Typography>
          {it.sub ? (
            <Typography noWrap sx={{ position: 'relative', fontSize: 12, color: 'text.disabled', pr: 1.5 }}>
              {it.sub}
            </Typography>
          ) : null}
          <Typography sx={{ position: 'relative', fontSize: 13, fontWeight: 600, pr: 1.25, fontVariantNumeric: 'tabular-nums' }}>
            {formatValue(it.value)}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

/** Thin proportion bar split into labeled segments (for status mix). */
export function SegmentBar({ parts }) {
  const total = parts.reduce((a, p) => a + (Number(p.value) || 0), 0);
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: '2px', height: 8, borderRadius: 99, overflow: 'hidden', bgcolor: 'action.hover' }}>
        {total
          ? parts
              .filter((p) => p.value > 0)
              .map((p) => <Box key={p.key} sx={{ flex: p.value, bgcolor: p.color }} />)
          : null}
      </Box>
      <Stack direction="row" sx={{ flexWrap: 'wrap', columnGap: 2.5, rowGap: 1, mt: 1.5 }}>
        {parts.map((p) => (
          <Stack key={p.key} direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: p.color }} />
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{p.label}</Typography>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {fmtNumber(p.value)}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{fmtPercent(p.value, total)}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
